import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ConnectWhatsApp from '../../../components/whatsapp/ConnectWhatsApp';
import { useTutorialOptional } from '../../../features/tutorial/TutorialContext';
import licenseService from '../../../services/licenseService';
import LicenseUsageMeter from '../../../components/license/LicenseUsageMeter';
import { formatCurrencyBRL } from '../../../utils/formatCurrencyBRL';
import { subscriptionChargeLabel } from '../../../utils/subscriptionChargeLabel';

const formatBrazilianDate = (isoDate) => {
  if (!isoDate) return null;

  return new Date(isoDate).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
  });
};

const isExpiringSoonDate = (isoDate, daysThreshold = 5) => {
  if (!isoDate) return false;

  const expiryDate = new Date(isoDate);
  if (Number.isNaN(expiryDate.getTime())) return false;

  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays >= 0 && diffDays <= daysThreshold;
};

function pickNeoCoinsDisplay(user) {
  if (!user || typeof user !== 'object') return '0';
  const raw = user.neo_coins ?? user.neoCoins ?? user.neo_coin ?? user.neoCoin;
  if (raw == null || raw === '') return '0';
  const n = Number(raw);
  return Number.isFinite(n) ? String(Math.trunc(n)) : String(raw);
}

const ContractMetrics = ({ user, showWhatsAppConnect = true }) => {
  const navigate = useNavigate();
  const tutorial = useTutorialOptional();
  const [subscription, setSubscription] = useState(null);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await licenseService.getMySubscription();
      if (!cancelled && response?.success) {
        setSubscription(response.subscription || null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;

  const { email, activeLicense } = user;

  const hasSubscription = Boolean(subscription);
  const hasAnyLicense = Boolean(activeLicense);
  const isExpired = hasAnyLicense && activeLicense?.is_active === 'false';
  const isExpiringSoon =
    hasAnyLicense && !isExpired && isExpiringSoonDate(activeLicense?.expires_at, 5);
  const isSubscriptionCancelled = Boolean(subscription?.cancel_at_period_end);

  const licenseCardClassName = isExpired
    ? 'rounded-xl border border-error/30 bg-error/10 p-4 flex gap-3'
    : isExpiringSoon || isSubscriptionCancelled
      ? 'rounded-xl border border-warning/30 bg-warning/10 p-4 flex gap-3'
      : 'rounded-xl border border-border bg-muted/30 p-4 flex gap-3 lg:bg-card';

  const licenseIconName = hasSubscription
    ? 'Calendar'
    : isExpired
      ? 'AlertCircle'
      : isExpiringSoon
        ? 'Clock'
        : 'BadgeCheck';
  const licenseIconClassName = isExpired
    ? 'text-error'
    : isExpiringSoon || isSubscriptionCancelled
      ? 'text-warning'
      : hasAnyLicense || hasSubscription
        ? 'text-success'
        : 'text-muted-foreground';

  const detailTextClassName = isExpired
    ? 'text-error'
    : isExpiringSoon || isSubscriptionCancelled
      ? 'text-warning'
      : 'text-muted-foreground';

  const activeLicenseDocumentsGenerated = activeLicense?.documents_generated ?? 0;
  const activeLicenseDocumentsSigned = activeLicense?.documents_signed ?? 0;
  const maxDocuments = activeLicense?.max_documents;
  const maxSignatures = activeLicense?.max_signatures;
  const neoCoinsDisplay = pickNeoCoinsDisplay(user);

  const subscriptionStatusLabel = isSubscriptionCancelled
    ? 'Assinatura cancelada — acesso até o fim do período'
    : subscription?.status === 'authorized' || subscription?.status === 'active'
      ? 'Assinatura ativa'
      : 'Assinatura';

  const licenseStatusLabel = isExpired ? 'Licença expirada' : 'Licença ativa';
  const expiryLabel = isExpired ? 'Expirou em:' : 'Expira em:';
  const expiryValueClassName = isExpired ? 'text-error' : 'text-foreground';

  return (
    <div className="mb-6 md:mb-8">
      <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-elevation-sm lg:shadow-elevation-md">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between lg:gap-8 mb-8">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-foreground tracking-tight">
              Bem-vindo, {email}
            </h1>

            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Aqui está um resumo da sua conta, licença e assinatura atual.
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end lg:w-auto lg:max-w-3xl lg:shrink-0">
            {showWhatsAppConnect ? (
              <Button
                type="button"
                size="lg"
                variant="outline"
                iconName="MessageCircle"
                data-tour="tour-connect-whatsapp"
                className="w-full sm:w-auto"
                aria-expanded={whatsAppOpen}
                onClick={() => setWhatsAppOpen((open) => !open)}
              >
                Conectar WhatsApp
              </Button>
            ) : null}
            {tutorial?.startTutorial ? (
              <Button
                type="button"
                size="lg"
                variant="outline"
                iconName="GraduationCap"
                data-tour="tour-show-tutorial"
                className="w-full sm:w-auto"
                onClick={() => tutorial.startTutorial()}
              >
                Mostrar tutorial
              </Button>
            ) : null}
            <Button
              type="button"
              size="lg"
              iconName="FilePlus"
              data-tour="tour-create-from-model"
              className="w-full shadow-md sm:min-w-[220px] sm:w-auto"
              onClick={() => navigate('/contract-generator')}
            >
              Criar a partir de Modelo
            </Button>
          </div>
        </div>

        {showWhatsAppConnect && whatsAppOpen ? (
          <div className="mb-8">
            <ConnectWhatsApp variant="panel" />
          </div>
        ) : null}

        <div
          data-tour="tour-metrics"
          className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6"
        >
          <div className={licenseCardClassName}>
            <div className="mt-0.5">
              <Icon
                name={licenseIconName}
                size={18}
                className={licenseIconClassName}
              />
            </div>

            <div className="min-w-0">
              {hasSubscription ? (
                <>
                  <p className={`text-xs font-medium mb-1 ${detailTextClassName}`}>
                    {subscriptionStatusLabel}
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {subscription.license_name}
                  </p>
                  <p className={`text-xs ${detailTextClassName} mt-1`}>
                    {formatCurrencyBRL(subscription.amount)}/mês
                  </p>
                  {subscription.next_payment_date && !isSubscriptionCancelled && (
                    <p className={`text-xs ${detailTextClassName} mt-1`}>
                      {subscriptionChargeLabel(subscription)}:{' '}
                      <span className="font-medium text-foreground">
                        {formatBrazilianDate(subscription.next_payment_date)}
                      </span>
                    </p>
                  )}
                  {activeLicense?.expires_at && (
                    <p className={`text-xs ${detailTextClassName} mt-1`}>
                      Acesso até:{' '}
                      <span className="font-medium text-foreground">
                        {formatBrazilianDate(activeLicense.expires_at)}
                      </span>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className={`text-xs font-medium mb-1 ${detailTextClassName}`}>
                    {licenseStatusLabel}
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {hasAnyLicense
                      ? activeLicense?.license_name
                      : 'Sem licença ativa atualmente'}
                  </p>
                  {hasAnyLicense && (
                    <>
                      <p className={`text-xs ${detailTextClassName} mt-1`}>
                        Tipo: {activeLicense.license_type}
                      </p>
                      {isExpiringSoon && (
                        <p className="text-xs text-warning mt-1 font-medium">
                          Expirando em breve
                        </p>
                      )}
                      {activeLicense.expires_at && (
                        <p className={`text-xs ${detailTextClassName} mt-1`}>
                          {expiryLabel}{' '}
                          <span className={`font-medium ${expiryValueClassName}`}>
                            {formatBrazilianDate(activeLicense.expires_at)}
                          </span>
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {(hasSubscription || hasAnyLicense) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  iconName={hasSubscription ? 'Calendar' : 'BadgeCheck'}
                  className="mt-3"
                  onClick={() => navigate('/my-license')}
                >
                  {hasSubscription ? 'Mostrar assinatura' : 'Mostrar Licença'}
                </Button>
              )}

              <div className="mt-3 pt-3 border-t border-border/70">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Icon
                    name="Coins"
                    size={16}
                    className="text-amber-500 shrink-0"
                    aria-hidden
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    Neo Coins
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-foreground">
                    {neoCoinsDisplay}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-muted/30 p-4 flex gap-3 lg:bg-card min-w-0">
            <div className="mt-0.5">
              <Icon name="FileText" size={18} className="text-primary" />
            </div>

            <div className="min-w-0 flex-1 space-y-4">
              {hasAnyLicense && !isExpired ? (
                <>
                  <LicenseUsageMeter
                    label="Documentos gerados"
                    usedRaw={activeLicenseDocumentsGenerated}
                    maxRaw={maxDocuments}
                  />
                  <LicenseUsageMeter
                    label="Assinaturas de documentos"
                    usedRaw={activeLicenseDocumentsSigned}
                    maxRaw={maxSignatures}
                  />
                </>
              ) : (
                <>
                  <p className="text-xs font-medium text-muted-foreground">
                    Documentos gerados (Licença ativa)
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {activeLicenseDocumentsGenerated}
                  </p>
                  <p className="text-xs font-medium text-muted-foreground mt-3 mb-1">
                    Assinaturas de documentos (Licença ativa)
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {activeLicenseDocumentsSigned}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractMetrics;
